import { Button } from '@mantine/core';
import { useDebouncedValue } from '@mantine/hooks';
import {
    BaseLayout,
    ContentHeader,
    EmptyState,
    ErrorView,
    getExceptionDetails,
    Icon,
    Inbox,
    InboxGroup,
    InboxItem,
    InboxItemType,
    LoadingAnimation,
    variants,
} from '@uag/react-core';
import { OnboardingRequestModel, RequestStateType } from '@uag/welcome-shared/src/api/models';
import { useGetRequests } from '@uag/welcome-shared/src/api/queries/requests/requests';
import { clsx } from 'clsx';
import { useAtom } from 'jotai';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Outlet, useLocation, useNavigate } from 'react-router';

import { managementInstance } from 'i18n';
import { AppliedFiltersType, getDefaultAppliedFilters } from './filter/AppliedFiltersType';
import { InboxFilter } from './filter/InboxFilter';
import { openInviteCompanyModal } from './modals/InviteCompanyModal';
import { searchTextAtom } from './RequestAtoms';

export const RequestsBaseRoute = '/requests';

const getStatus = (state: RequestStateType | null | undefined) => {
    switch (state) {
        case RequestStateType.Invited:
        case RequestStateType.DataInput:
        case RequestStateType.AdditionalDataInput:
        case RequestStateType.ContractSigning:
        case RequestStateType.CoreSystemActivation:
        case RequestStateType.ServiceActivation:
            return undefined;
        case RequestStateType.SignatureVerification:
        case RequestStateType.DataVerification:
            return 'warning';
        case RequestStateType.Completed:
        case RequestStateType.ShopActivationCompleted:
        case RequestStateType.CompletedWithoutMyDoka:
        case RequestStateType.Doka360AccessRequested:
            return 'success';
        case RequestStateType.Declined:
        case RequestStateType.Aborted:
        case RequestStateType.Expired:
            return 'alert';
        default:
            throw new Error(`Unknown state: ${state}`);
    }
};

type OnboardingInboxItem = InboxItemType & {
    id: string;
};

const getRequestQueryParams = (filters: AppliedFiltersType, customerSearchText: string | undefined) => ({
    state: filters.state ?? undefined,
    requestTypes: filters.requestType ?? undefined,
    customerSearchText,
    branchIds: filters.branch ? [filters.branch] : undefined,
    contributorIds: filters.responsiblePerson ? [filters.responsiblePerson] : undefined,
});

const getInboxItem = (data: OnboardingRequestModel): OnboardingInboxItem => ({
    id: data.id,
    name: data.dokaOnboardingResponsible?.model.displayName,
    img: data.dokaOnboardingResponsible?.model.profilePictureUri ?? undefined,
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    company: data.companyData.companyName!,
    place: data.branch?.branchName ?? undefined,
    time: new Date(data.createdDate),
    status: {
        status: getStatus(data.currentState),
        message: (data.currentState && managementInstance.t(`RequestState.${data.currentState}`)) ?? '',
    },
});

export const Requests = () => {
    const { t } = useTranslation();
    const location = useLocation();
    const navigate = useNavigate();

    const [searchText, setSearchText] = useAtom(searchTextAtom);
    const [debounceSearchText] = useDebouncedValue(searchText, 500);
    const [appliedFilters, setAppliedFilters] = useState<AppliedFiltersType>(getDefaultAppliedFilters());
    const normalizedSearch = debounceSearchText?.trim() ? debounceSearchText.trim() : undefined;
    const [suppressAutoNav, setSuppressAutoNav] = useState(false);

    useEffect(() => {
        // eslint-disable-next-line react-hooks/set-state-in-effect
        setSuppressAutoNav(false);
    }, [normalizedSearch]);

    const { data, isLoading, error } = useGetRequests(getRequestQueryParams(appliedFilters, debounceSearchText), {
        query: { refetchOnWindowFocus: true },
    });

    const filteredItems = useMemo(() => {
        const hideDataInput = !appliedFilters.state?.length && !normalizedSearch;
        return (data?.data ?? []).filter((item) =>
            hideDataInput ? item.currentState !== RequestStateType.DataInput : true,
        );
    }, [data?.data, appliedFilters.state, normalizedSearch]);

    const completedItems = useMemo(() => {
        return filteredItems.filter(
            (item) =>
                item.currentState === RequestStateType.Completed
                || item.currentState === RequestStateType.CompletedWithoutMyDoka
                || item.currentState === RequestStateType.ShopActivationCompleted
                || item.currentState === RequestStateType.Declined
                || item.currentState === RequestStateType.Expired
                || item.isHidden,
        );
    }, [filteredItems]);

    const ongoingItems = useMemo(() => {
        return filteredItems.filter((item) => !completedItems.includes(item));
    }, [filteredItems, completedItems]);

    const atBaseRoute = location.pathname === RequestsBaseRoute;
    const endsWithBase = location.pathname.endsWith(RequestsBaseRoute);
    const currentId = useMemo(() => location.pathname.split('/').pop(), [location.pathname]);

    const filteredIds = useMemo(() => filteredItems.map((i) => i.id), [filteredItems]);
    const newestFilteredId = useMemo(() => {
        if (!filteredItems.length) {
            return undefined;
        }
        const newest = [...filteredItems].sort(
            (a, b) => new Date(b.createdDate).getTime() - new Date(a.createdDate).getTime(),
        )[0];
        return newest?.id;
    }, [filteredItems]);

    const currentExistsInFiltered = useMemo(
        () => (currentId ? filteredIds.includes(currentId) : false),
        [currentId, filteredIds],
    );

    const hasAnyItems = ongoingItems.length > 0 || completedItems.length > 0;

    const itemGroups = useMemo(
        () => [
            {
                id: 'ongoing',
                title: t('toBeEdited'),
                items: [...ongoingItems].sort((a, b) => {
                    const dateA = a.createdDate ? new Date(a.createdDate).getTime() : 0;
                    const dateB = b.createdDate ? new Date(b.createdDate).getTime() : 0;
                    return dateB - dateA;
                }),
            },
            {
                id: 'completed',
                title: t('completedOrHidden'),
                items: [...completedItems].sort((a, b) => {
                    const dateA = a.createdDate ? new Date(a.createdDate).getTime() : 0;
                    const dateB = b.createdDate ? new Date(b.createdDate).getTime() : 0;
                    return dateB - dateA;
                }),
            },
        ],
        [t, ongoingItems, completedItems],
    );

    const openItem = (item: OnboardingInboxItem) => {
        setSuppressAutoNav(true);
        navigate(`${RequestsBaseRoute}/${item.id}`);
    };

    useEffect(() => {
        if (!isLoading && !hasAnyItems && !atBaseRoute) {
            navigate(RequestsBaseRoute);
        }
    }, [isLoading, hasAnyItems, atBaseRoute, navigate]);

    useEffect(() => {
        if (
            normalizedSearch
            && filteredIds.length > 0
            && !currentExistsInFiltered
            && !suppressAutoNav
            && newestFilteredId
            && !location.pathname.endsWith(newestFilteredId)
        ) {
            navigate(`${RequestsBaseRoute}/${newestFilteredId}`);
            localStorage.setItem('lastOpenedRequestId', newestFilteredId);
        }
    }, [
        normalizedSearch,
        filteredIds.length,
        currentExistsInFiltered,
        suppressAutoNav,
        newestFilteredId,
        location.pathname,
        navigate,
    ]);

    useEffect(() => {
        if (!normalizedSearch && data?.data?.length && endsWithBase) {
            navigate(`${RequestsBaseRoute}/${data.data[0].id}`);
        }
    }, [normalizedSearch, data?.data?.length, data?.data, endsWithBase, navigate]);

    const handleInviteCompany = () => {
        openInviteCompanyModal();
    };

    const isEmpty = !isLoading && ongoingItems.length === 0 && completedItems.length === 0;

    return (
        <BaseLayout versionNumber={APP_VERSION}>
            {error ? (
                <ErrorView description={getExceptionDetails(error)?.message} />
            ) : (
                <>
                    <Inbox>
                        <ContentHeader title={t('requests')}>
                            <ContentHeader.Actions>
                                <Button variant={variants.button.secondary} onClick={handleInviteCompany}>
                                    {t('inviteCompany')}
                                    <Icon className="ml-2" icon="add" />
                                </Button>
                            </ContentHeader.Actions>
                        </ContentHeader>

                        <InboxFilter
                            appliedFilters={appliedFilters}
                            searchText={searchText}
                            setAppliedFilters={setAppliedFilters}
                            setSearchText={setSearchText}
                        />

                        {isLoading && <LoadingAnimation />}
                        <div className={clsx('inbox-entries', isEmpty && 'h-5/6')}>
                            {isEmpty ? (
                                <EmptyState className="flex h-full items-center justify-center" type="search">
                                    <span>{t('nothingFoundMessage')}</span>
                                </EmptyState>
                            ) : (
                                !isLoading
                                && itemGroups.map((group) => (
                                    <InboxGroup key={group.id} title={group.title as string}>
                                        {group.items.map((item) => (
                                            <InboxItem
                                                key={item.id}
                                                data={getInboxItem(item)}
                                                isSelected={location.pathname.endsWith(item.id)}
                                                onClick={openItem}
                                            />
                                        ))}
                                    </InboxGroup>
                                ))
                            )}
                        </div>
                    </Inbox>
                    <div className="p-base w-full">{!isLoading && <Outlet />}</div>
                </>
            )}
        </BaseLayout>
    );
};
